import ImageLayer from 'ol/layer/Image';
import Collection from 'ol/Collection';
import { listen, unlistenByKey } from 'ol/events';
import RenderEventType from 'ol/render/EventType';
import VectorImageLayer from 'ol/layer/VectorImage';
import Translate from 'ol/interaction/Translate';

import { MapProperty } from 'cadenza/map/map-property';
import { assert } from 'cadenza/utils/custom-error';
import { ModifyInteraction } from 'cadenza/map/interactions/modify-interaction';
import { ClassicSketchApi } from 'cadenza/api-client/classic-sketch-api';
import { SketchLayerProperty } from 'cadenza/map/classic-sketch-layer/sketch-layer-property';
import { CommonDrawInteractionGroup } from 'cadenza/map/interactions/common-draw-interaction-group';
import { modelStyleToOpenLayersStyle, getMainStyle } from 'cadenza/map/classic-sketch-layer/sketch-style';
import { sketchTypeToOpenLayersGeometryType } from 'cadenza/map/classic-sketch-layer/type-mapper';
import { clearAndRefreshVectorLayer, refreshLayer } from 'cadenza/map/layer-utils';
import { SketchLayerSource, FEATURE_MIN_Z_INDEX_VALUE } from 'cadenza/map/classic-sketch-layer/sketch-layer-source';
import { TranslateByHandleInteraction } from 'cadenza/map/interactions/translate-by-handle-interaction';
import { SketchModifyInteractionGroup } from 'cadenza/map/classic-sketch-layer/interactions/sketch-modify-interaction-group';

export class ClassicSketchLayer extends ImageLayer {

  constructor ({ source, layerId, map }) {
    super({ source });
    this._map = map;
    this._editing = false;
    this._sketchApi = new ClassicSketchApi(layerId);
    this._featureLayer = new VectorImageLayer({
      source: new SketchLayerSource({ layerId }),
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      // OL layer renderer is always sorting layer features before rendering. By default the ol.renderer.vector.defaultOrder function is used, which sorts the features by ol_uid.
      // Sketch layer feature order is defined by the order of features in the source.getFeatures() array. But all features also get a zIndex, which is set based on the array order, see SketchLayerSource:change.
      // We cannot prevent OL from resorting of the array, so we define a sorting function considering sketch layer feature order.
      renderOrder: (feature1, feature2) => {
        return getMainStyle(feature1).getZIndex() - getMainStyle(feature2).getZIndex();
      }
    });

    this._showWms = () => {
      this.setSource(source);
      this.setOpacity(1);
    };
    this._hideWms = () => {
      this.setOpacity(0);
      this.setSource(null); // Prevent GetMap requests while editing
    };
    this._source = source;
  }

  // Avoids some NPE when getting the source of this layer, for example: sketch-dialog::registerLayerEvents
  getSource () {
    return this._source;
  }

  setEditing (editing) {
    if (this._editing !== editing) {
      this._editing = editing;
      if (editing) {
        this.un('change', this._showWms);
        this._hideWms();
        this._featureLayer.setMap(this._map);

        // Sketch feature layer is an overlay layer and normally such layers are marked as not "managed" by OL.
        // That means, that sometimes, the features of the layer cannot be hit or redrawn during interacting.
        // So we explicitly set the layer to be managed, but keep it out of the map layer list.
        this._mapPrecomposeKey = listen(this._map, RenderEventType.PRECOMPOSE, () => { this._featureLayer.getLayerState().managed = true; });
      } else {
        unlistenByKey(this._mapPrecomposeKey);
        this._featureLayer.setMap(null);
        clearAndRefreshVectorLayer(this._featureLayer);
        this._source.once('change', this._showWms);
        refreshLayer(this);
      }
      this.dispatchEvent('change:editing');
    }
  }

  isEditing () {
    return this._editing;
  }

  enableDrawInteraction (paletteItem) {
    const sketchDrawInteraction = new CommonDrawInteractionGroup(paletteItem, this._featureLayer.getSource(),
      this._map, {
        type: sketchTypeToOpenLayersGeometryType(paletteItem),
        featureStyle: modelStyleToOpenLayersStyle(paletteItem)
      }
    );
    this._map.set(MapProperty.PRIMARY_INTERACTION, sketchDrawInteraction);
  }

  enableModifyInteraction () {
    const sketchModifyInteraction = new SketchModifyInteractionGroup(this._featureLayer, { withTranslateByHandle: true, map: this._map });
    this._map.set(MapProperty.PRIMARY_INTERACTION, sketchModifyInteraction);
    this._map.once(`change:${MapProperty.PRIMARY_INTERACTION}`, () => sketchModifyInteraction.dispose());
  }

  saveFeatures () {
    const features = this._featureLayer.getSource().getFeatures();
    this.set(SketchLayerProperty.IS_SAVING, true);
    return this._sketchApi.save(features).finally(() => this.set(SketchLayerProperty.IS_SAVING, false));
  }

  selectFeatures (featuresToSelect) {
    const modifyInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    if (modifyInteraction instanceof SketchModifyInteractionGroup) {
      const selectedFeaturesCollection = modifyInteraction.getSelectedFeatures();
      selectedFeaturesCollection.clear();
      featuresToSelect.forEach(feature => selectedFeaturesCollection.push(feature));
    }
  }

  unselectAllFeatures () {
    const modifyInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    if (modifyInteraction instanceof SketchModifyInteractionGroup) {
      modifyInteraction.getSelectedFeatures().clear();
    }
  }

  removeFeatures (features) {
    features.forEach(feature => {
      this._featureLayer.getSource().removeFeature(feature);
    });
  }

  addFeatures (features) {
    this._isSilentAdd = true;
    this._featureLayer.getSource().addFeatures(features);
    this._isSilentAdd = false;
  }

  isSilentAdd () {
    return this._isSilentAdd;
  }

  getSelectedFeatures () {
    const modifyInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    if (modifyInteraction instanceof SketchModifyInteractionGroup) {
      return modifyInteraction.getSelectedFeatures();
    } else {
      return new Collection();
    }
  }

  getModifyInteraction () {
    const sketchModifyInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    assert(sketchModifyInteraction instanceof SketchModifyInteractionGroup, 'Map primary interaction must be an instance of SketchModifyInteractionGroup');
    return sketchModifyInteraction.getInteractionByClass(ModifyInteraction);
  }

  getTranslateInteraction () {
    const sketchModifyInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    assert(sketchModifyInteraction instanceof SketchModifyInteractionGroup, 'Map primary interaction must be an instance of SketchModifyInteractionGroup');
    return sketchModifyInteraction.getInteractionByClass(Translate);
  }

  getTranslateByHandleInteraction () {
    const sketchModifyInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION);
    assert(sketchModifyInteraction instanceof SketchModifyInteractionGroup, 'Map primary interaction must be an instance of SketchModifyInteractionGroup');
    return sketchModifyInteraction.getInteractionByClass(TranslateByHandleInteraction);
  }

  getFeatureLayer () {
    return this._featureLayer;
  }

  isFeaturesLoading () {
    return this._featureLayer.getSource().get('loadend') === false;
  }

  areFeaturesInForeground (features) {
    const allFeatures = this._featureLayer.getSource().getFeatures();
    const zMax = Math.max(FEATURE_MIN_Z_INDEX_VALUE, ...allFeatures.map(feature => getMainStyle(feature).getZIndex()));
    return features.some(feature => getMainStyle(feature).getZIndex() === zMax);
  }

  areFeaturesInBackground (features) {
    return features.some(feature => getMainStyle(feature).getZIndex() === FEATURE_MIN_Z_INDEX_VALUE);
  }

  getPaletteItem () {
    const sketchDraw = this._map.get(MapProperty.PRIMARY_INTERACTION);
    assert(sketchDraw instanceof CommonDrawInteractionGroup, 'Map primary interaction must be an instance of CommonDrawInteractionGroup');
    return sketchDraw.getPaletteItem();
  }

  bringFeaturesToFront (features) {
    this.sortFeatures((a, b) => {
      if (features.includes(a)) {
        return 1;
      } else if (features.includes(b)) {
        return -1;
      }
      return getMainStyle(a).getZIndex() - getMainStyle(b).getZIndex();
    });
  }

  sendFeaturesToBack (features) {
    this.sortFeatures((a, b) => {
      if (features.includes(a)) {
        return -1;
      } else if (features.includes(b)) {
        return 1;
      }
      return getMainStyle(a).getZIndex() - getMainStyle(b).getZIndex();
    });
  }

  sortFeaturesByZIndex () {
    this.sortFeatures((a, b) => {
      return getMainStyle(a).getZIndex() - getMainStyle(b).getZIndex();
    });
  }

  setFeaturesOrder (featureOrder) {
    this.sortFeatures((a, b) => featureOrder.indexOf(a.ol_uid) - featureOrder.indexOf(b.ol_uid));
  }

  sortFeatures (sortFunction) {
    const source = this._featureLayer.getSource();
    source.getFeaturesCollection().getArray().sort(sortFunction);
    source.changed(); // Redraw the layer and reassign correct z-indexes see SketchLayerSource:change
  }

}
